import React, {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';

import { FullPageError } from 'src/components/full-page-error/FullPageError';

import { initCoralogixRum } from '../../services/analytics/CoralogixRumService';
import { isLoggedIn } from '../../services/AuthenticationService';
import {
	getCentralConfig,
	TConfiguration
} from '../../services/central-configuration/CentralConfigurationService';
import {
	getToggleFromLocalStorage,
	SiteScannerUiToggles
} from '../../services/LocalTogglesService';
import { logger } from '../../services/Logger';
import { useUserTenant } from '../userTenantProvider/UserTenantProvider';

import { CONFIGURATION_PROVIDER_LABELS } from './ConfigurationProvider.labels';

type TGetToggle = (
	toggleId: SiteScannerUiToggles,
	// useUserToggle: bc for auth0 use toggle support. Default is false
	// If true, will try to extract auth0 value toggle from the user's token
	// If false, will try to extract the value from the central configuration instead
	useUserToggle?: boolean
) => boolean;

interface IConfigurationContext {
	configuration: TConfiguration | null;
	getToggle: TGetToggle;
}

const ConfigurationContext = createContext<IConfigurationContext | null>(null);

const normalizeBooleanToggleValue = (toggle: string | boolean): string | boolean => {
	if (toggle === 'true') {
		return true;
	}
	if (toggle === 'false') {
		return false;
	}
	return toggle;
};

const ConfigurationProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [configuration, setConfiguration] = useState<TConfiguration | null>(null);
	const [hasConfigurationError, setHasConfigurationError] = useState(false);
	const { tenant } = useUserTenant();
	const hasInitializedCoralogix = useRef(false);

	const getToggle = useCallback(
		// turn `useAuth0Toggles` to false to make it use central configuration
		(toggleId: SiteScannerUiToggles, useAuth0Toggles = false): boolean => {
			const localToggleValue = getToggleFromLocalStorage(toggleId, useAuth0Toggles);
			return useAuth0Toggles
				? localToggleValue
				: localToggleValue ||
						!!normalizeBooleanToggleValue(configuration?.TOGGLES?.[toggleId] as string | boolean) ||
						!!normalizeBooleanToggleValue(
							configuration?.GLOBAL?.TOGGLES?.[toggleId] as string | boolean
						);
		},
		[configuration]
	);

	const initConfiguration = useCallback(async (): Promise<void> => {
		if (!configuration && tenant?.products) {
			try {
				const formattedConfig: TConfiguration = await getCentralConfig(tenant.products);
				if (!formattedConfig) {
					throw new Error('Central configuration response is empty');
				}
				setConfiguration(formattedConfig);
				setHasConfigurationError(false);

				if (!hasInitializedCoralogix.current) {
					hasInitializedCoralogix.current = true;
					initCoralogixRum();
				}
			} catch (error) {
				logger.error('Failed to initialize site scanner configuration', error);
				setHasConfigurationError(true);
			}
		}
	}, [configuration, tenant?.products]);

	useEffect(() => {
		if (isLoggedIn()) {
			initConfiguration();
		}
	}, [initConfiguration]);

	const value = useMemo(() => ({ configuration, getToggle }), [configuration, getToggle]);

	if (hasConfigurationError) {
		return <FullPageError title={CONFIGURATION_PROVIDER_LABELS.loadingErrorTitle} />;
	}

	return <ConfigurationContext.Provider value={value}>{children}</ConfigurationContext.Provider>;
};

const useConfiguration = (): IConfigurationContext => {
	const context = useContext(ConfigurationContext);

	if (!context) {
		throw new Error('useConfiguration must be used within ConfigurationProvider');
	}

	return context;
};

export { ConfigurationProvider, useConfiguration };
